// Class windows own their URL state; library and quiz windows keep their own keys.
export const classQueryNames = Object.freeze({
  folder: "classFolder", librarySection: "classSection", map: "classMap",
  node: "classNode", libraryNode: "classNode", practice: "classPractice",
  studyTab: "classStudyTab", cardsFilter: "classCardsFilter", quizFilter: "classQuizFilter",
  form: "classForm", assignment: "classAssignment", announcement: "classAnnouncement",
});
export const classStateKeys = Object.freeze([
  "classInstitution", "classCohort", "classroom", "classTab", "classResource", "classInvitation", "legacyClass",
  ...new Set(Object.values(classQueryNames)),
]);
const contentKeys = [...Object.values(classQueryNames), "classResource"];

export function studentClassesHref(current, selection = {}, resetContent = false) {
  const next = new URLSearchParams(current?.toString() || "");
  // Older library class links used this key; never turn the personal library into a class panel.
  if (next.get("librarySection") === "classes") next.delete("librarySection");
  if (resetContent) for (const key of contentKeys) next.delete(key);
  for (const [key, value] of Object.entries(selection)) {
    if (value == null || value === "") next.delete(key); else next.set(key, value);
  }
  const views = (next.get("views") || "").split(",").filter(Boolean);
  if (views.length && !views.includes("classes")) next.set("views", [...views, "classes"].join(","));
  return `/app/classes${next.size ? `?${next}` : ""}`;
}

export function classQueryHref(path, logicalParams, student) {
  const url = new URL(path, "https://navigation.local");
  if (!student) {
    url.search = logicalParams.toString();
  } else {
    // Read/write only class keys. In particular don't copy logical folder/map keys over a library pane.
    for (const [logical, physical] of Object.entries(classQueryNames)) {
      if (logical === "node") continue;
      const value = logicalParams.get(logical) ?? (logical === "libraryNode" ? logicalParams.get("node") : null);
      if (value == null) url.searchParams.delete(physical); else url.searchParams.set(physical, value);
    }
  }
  return url.pathname + url.search;
}

export function legacyClassesHref(pathname, params) {
  if (!/\/app\/library(?:\/|$)/.test(pathname) || params.get("librarySection") !== "classes") return null;
  const next = new URLSearchParams(params.toString());
  if (next.get("classroom") && next.get("libraryMap")) {
    next.set("classMap", next.get("libraryMap"));
    next.delete("libraryMap");
    for (const [legacy, current] of [["libraryNode", "classNode"], ["studyTab", "classStudyTab"], ["cardsFilter", "classCardsFilter"], ["quizFilter", "classQuizFilter"]]) {
      const value = next.get(legacy);
      if (value != null) { next.set(current, value); next.delete(legacy); }
    }
  }
  const views = (next.get("views") || "").split(",").filter(Boolean).map(view => view === "library" ? "classes" : view);
  if (views.length) next.set("views", [...new Set(views)].join(","));
  return studentClassesHref(next);
}

export function legacyPlatformStudentHref(institutionId, segments, params, classes = []) {
  const section = segments[0] || "turmas", selection = {classInstitution: institutionId};
  if (section === "turmas" && segments[1]) {
    if (segments[2] === "disciplinas") {
      selection.classCohort = segments[1]; selection.classroom = segments[3]; selection.classTab = segments[4] || "overview";
    } else {
      const subject = classes.find(item => item.id === segments[1]);
      if (subject) { selection.classroom = subject.id; selection.classCohort = subject.cohortId; selection.classTab = segments[2] || "overview"; }
      else { selection.classCohort = segments[1]; selection.classTab = segments[2] || "subjects"; }
    }
  } else if (section === "atividades" || section === "agenda" || section === "formularios") selection.classTab = section === "atividades" ? "activities" : section === "formularios" ? "forms" : "agenda";
  if (section === "formularios" && segments[1]) selection.classForm = segments[1];
  const physical = new URLSearchParams();
  for (const key of ["views", "dock", "topbar"]) { const value = params.get(key); if (value) physical.set(key, value); }
  return classQueryHref(studentClassesHref(physical, selection), params, true);
}

export function classLogicalSearch(params, student) {
  const next = new URLSearchParams(params.toString());
  if (student) for (const [logical, physical] of Object.entries(classQueryNames)) {
    if (logical === "node") { next.delete(logical); continue; }
    const value = params.get(physical);
    if (value == null) next.delete(logical); else next.set(logical, value);
  }
  return next;
}

export function studentPlatformHref(institutionId, params, classes = []) {
  const classroomId = params.get("classroom"), cohortId = params.get("classCohort");
  const subject = classes.find(item => item.id === classroomId);
  const cohort = subject?.cohortId || cohortId, tab = params.get("classTab");
  let path = `/platform/${institutionId}/classes`;
  if (classroomId) path += cohort ? `/${cohort}/subjects/${classroomId}/${tab || "overview"}` : `/${classroomId}/${tab || "overview"}`;
  else if (cohortId) path += `/${cohortId}${tab && tab !== "subjects" ? `/${tab}` : ""}`;
  else if (tab === "activities" || tab === "agenda" || tab === "forms") path = `/platform/${institutionId}/${tab === "activities" ? "activities" : tab === "forms" ? "forms" : "agenda"}`;
  const logical = classLogicalSearch(params, true), query = new URLSearchParams();
  for (const key of Object.keys(classQueryNames)) { const value = logical.get(key); if (value != null) query.set(key, value); }
  return path + (query.size ? `?${query}` : "");
}
